import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { BusinessSettingsLayout } from '@components/layout/BusinessSettingsLayout';
import { BrandLoader } from '@components/ui/BrandLoader';
import { Button } from '@components/ui/Button';
import { ScheduleRow } from '@features/onboarding/components/ScheduleRow';
import { DAYS_OF_WEEK, DEFAULT_SCHEDULE } from '@features/onboarding/types';
import type { DayOfWeek, DaySchedule, ScheduleData } from '@features/onboarding/types';
import { mapSettingsError, useBusinessHours, scheduleToApi } from '@features/settings';

// ─── Main component ───────────────────────────────────────────────────────────

export function BusinessHoursPage() {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ScheduleData>(DEFAULT_SCHEDULE);

  const { schedule, timezone, isLoading, fetchError, update, isUpdating, updateError } =
    useBusinessHours();

  useEffect(() => {
    if (schedule) setDraft(schedule);
  }, [schedule]);

  function handleChange(day: DayOfWeek, updated: DaySchedule) {
    setDraft((prev) => ({ ...prev, [day]: updated }));
  }

  function handleDiscard() {
    if (schedule) setDraft(schedule);
  }

  const isDirty = useMemo(
    () => schedule != null && JSON.stringify(draft) !== JSON.stringify(schedule),
    [draft, schedule],
  );

  const hasError = DAYS_OF_WEEK.some((day) => {
    const s = draft[day];
    return s.open && s.from >= s.to;
  });

  function handleSave() {
    update(scheduleToApi(draft, timezone));
  }

  return (
    <BusinessSettingsLayout>
      {isLoading ? (
        <BrandLoader
          title={t('settings.loading.title')}
          subtitle={t('settings.loading.subtitle')}
        />
      ) : fetchError ? (
        <div className="rounded-md border border-border bg-surface px-6 py-12 text-center">
          <p className="text-sm text-destructive">
            {mapSettingsError(fetchError, t, 'settings.businessHours.errors.loadFailed')}
          </p>
        </div>
      ) : (
        <div className="rounded-md border border-border bg-surface">
          <div className="border-b border-border px-4 py-3 sm:px-6 sm:py-4">
            <h2 className="text-base font-semibold text-foreground">
              {t('settings.businessHours.title')}
            </h2>
          </div>

          <div className="px-4 py-5 sm:px-6 sm:py-6">
            <h3 className="mb-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('settings.businessHours.weeklySchedule')}
            </h3>
            <div className="space-y-2">
              {DAYS_OF_WEEK.map((day) => (
                <ScheduleRow
                  key={day}
                  day={day}
                  schedule={draft[day]}
                  onChange={handleChange}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-y-3 border-t border-border px-4 py-3 sm:px-6 sm:py-4">
            <div>
              {updateError ? (
                <p className="text-sm text-destructive">
                  {mapSettingsError(updateError, t, 'settings.businessHours.errors.saveFailed')}
                </p>
              ) : isDirty ? (
                <p className="text-sm text-muted-foreground">
                  {t('settings.unsavedChanges')}
                </p>
              ) : null}
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="ghost" onClick={handleDiscard} disabled={isUpdating}>
                {t('settings.businessHours.cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={!isDirty || hasError}
                isLoading={isUpdating}
              >
                {t('settings.businessHours.save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </BusinessSettingsLayout>
  );
}
